// src/App.tsx (Complete with all features)
import React, { useMemo, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { AppBar, Box, Toolbar, Typography, Button, Container, Grid, ThemeProvider, createTheme, CssBaseline, IconButton, Tooltip } from '@mui/material';
import { Facebook, WhatsApp, DarkMode, LightMode } from '@mui/icons-material';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import NotificationSystem from './components/NotificationSystem';
import logo from './images/neighborhoodpulse_sa_logo.png';
import ContactUs from './pages/ContactUs';
import AboutUs from './pages/AboutUs';
import FixedSidebar from './components/FixedSidebar';

// Import all pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Announcements from './pages/Announcements';
import ServiceProviders from './pages/ServiceProviders';
import AdminDashboard from './pages/AdminDashboard';
import UserManagement from './pages/UserManagement';
import Reviews from './pages/Reviews';
import ReviewManagement from './pages/ReviewManagement';
import Bookings from './pages/Bookings';
import ProviderRegister from './pages/ProviderRegister';
import Categories from './pages/Categories';
import FAQ from './pages/FAQ';
import ProviderDashboard from './pages/ProviderDashboard';
import ProviderDetails from './pages/ProviderDetails';
import AdminPanel from './pages/AdminPanel';
import AdminPromotions from './pages/AdminPromotions';
import TransferHub from './pages/TransferHub';
import TransferHubProfile from './pages/TransferHubProfile';
import TransferHubRequests from './pages/TransferHubRequests';
import TransferHubMatches from './pages/TransferHubMatches';
// import TransferHubMessages from './pages/TransferHubMessages';
import TransferHubSearch from './pages/TransferHubSearch';
import TransferHubJobs from './pages/TransferHubJobs';
import TransferHubLanding from './pages/TransferHubLanding';
import TransferHubProfileView from './pages/TransferHubProfileView';
import NewMessaging from './components/NewMessaging';
import NewMatching from './components/NewMatching';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancelled from './pages/PaymentCancelled';

const Footer: React.FC = () => (
  <Box component="footer" sx={{ bgcolor: 'background.paper', py: 3, mt: 6, borderTop: 1, borderColor: 'divider' }}>
    <Container maxWidth="lg">
      <Grid container spacing={2} justifyContent="center">
        <Grid item><Button color="inherit" component={Link} to="/">Home</Button></Grid>
        <Grid item><Button color="inherit" component={Link} to="/providers">Browse Services</Button></Grid>
        <Grid item><Button color="inherit" component={Link} to="/provider-register">For Service Providers</Button></Grid>
        <Grid item><Button color="inherit" component={Link} to="/about">About Us</Button></Grid>
        <Grid item><Button color="inherit" component={Link} to="/contact">Contact Us</Button></Grid>
        <Grid item><Button color="inherit" component={Link} to="/faq">FAQ</Button></Grid>
      </Grid>
      <Grid container spacing={2} justifyContent="center" sx={{ mt: 2 }}>
        <Grid item>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Facebook />}
            href="https://www.facebook.com/profile.php?id=61580695743715"
            target="_blank"
            rel="noopener noreferrer"
          >
            NeighborhoodPulse Facebook
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<WhatsApp />}
            href="https://whatsapp.com/channel/0029Vb6FFs5HVvTRCGG2M93Y"
            target="_blank"
            rel="noopener noreferrer"
          >
            NeighborhoodPulse WhatsApp
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Facebook />}
            href="https://www.facebook.com/profile.php?id=61580648057155"
            target="_blank"
            rel="noopener noreferrer"
          >
            TransferHub Facebook
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<WhatsApp />}
            href="https://whatsapp.com/channel/0029Vb6h4mFCBtxAbD0MPV1M"
            target="_blank"
            rel="noopener noreferrer"
          >
            TransferHub WhatsApp
          </Button>
        </Grid>
      </Grid>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
        © {new Date().getFullYear()} NeighborhoodPulse_SA. All rights reserved.
      </Typography>
    </Container>
  </Box>
);

interface AppLayoutProps {
  children: React.ReactNode;
  mode: 'light' | 'dark';
  onToggleTheme: () => void;
}

// AppLayout contains the navigation and uses the auth context
const AppLayout: React.FC<AppLayoutProps> = ({ children, mode, onToggleTheme }) => {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNotificationClick = (notification: any) => {
    // Handle notification clicks - navigate to relevant pages
    if (notification.type === 'announcement') {
      navigate('/announcements');
    } else if (notification.type === 'provider') {
      navigate('/providers');
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <FixedSidebar />
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppBar position="static">
          <Container maxWidth="lg">
            <Toolbar disableGutters>
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                <img src={logo} alt="NeighborhoodPulse Logo" style={{ height: 40, marginRight: 8 }} />
                <Typography variant="h6" component={Link} to="/" sx={{ color: 'inherit', textDecoration: 'none' }}>
                  NeighborhoodPulse
                </Typography>
              </Box>
              
              {/* Main navigation */}
              <Button color="inherit" component={Link} to="/">Home</Button>
              <Button color="inherit" component={Link} to="/announcements">Announcements</Button>
              <Button color="inherit" component={Link} to="/providers">Providers</Button>
              <Button color="inherit" component={Link} to="/bookings">Bookings</Button>
              <Button color="inherit" component={Link} to="/messages">Messages</Button>
              <Button color="inherit" component={Link} to="/transferhub">TransferHub</Button>
              <Button color="inherit" component={Link} to="/blog">Blog</Button>
              
              {/* Auth controls on the right */}
              <Box sx={{ flexGrow: 1 }} />
              <Tooltip title={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
                <IconButton color="inherit" onClick={onToggleTheme}>
                  {mode === 'light' ? <DarkMode /> : <LightMode />}
                </IconButton>
              </Tooltip>
              {isAuthenticated && (
                <>
                  <NotificationSystem onNotificationClick={handleNotificationClick} />
                  <Button color="inherit" onClick={handleLogout}>Logout</Button>
                </>
              )}

              {!isAuthenticated && (
                <>
                  <Button color="inherit" component={Link} to="/login">Login</Button>
                  <Button color="inherit" component={Link} to="/register">Register</Button>
                </>
              )}
            </Toolbar>
          </Container>
        </AppBar>

        {/* Secondary nav for TransferHub */}
        {location.pathname.startsWith('/transferhub') && (
          <AppBar position="static" color="secondary" elevation={0}>
            <Container maxWidth="lg">
              <Toolbar variant="dense" disableGutters sx={{ bgcolor: '#f0f8ff', px: 2, borderRadius: 1, border: '1px solid #e3f2fd' }}>
                <Button color="inherit" component={Link} to="/transferhub">TransferHub</Button>
                <Button color="inherit" component={Link} to="/transferhub/profiles">Profiles</Button>
                <Button color="inherit" component={Link} to="/transferhub/profile">My Profile</Button>
                <Button color="inherit" component={Link} to="/transferhub/jobs">Jobs</Button>
                <Button color="inherit" component={Link} to="/transferhub/requests">Requests</Button>
                <Button color="inherit" component={Link} to="/transferhub/matches">Matches</Button>
                <Button color="inherit" component={Link} to="/transferhub/conversations">Messages</Button>
                <Button color="inherit" component={Link} to="/transferhub/search">Search</Button>
              </Toolbar>
            </Container>
          </AppBar>
        )}
        
        <Box sx={{ flexGrow: 1, p: 3 }}>
          <Container maxWidth="lg">
            <Box>{children}</Box>
          </Container>
        </Box>
        
        <Footer />
      </Box>
    </Box>
  );
};

// Main App component sets up the router and providers
function App() {
  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    const stored = localStorage.getItem('themeMode');
    return stored === 'dark' ? 'dark' : 'light';
  });

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: '#1134A6'
          },
          secondary: {
            main: '#0F52BA'
          }
        }
      }),
    [mode]
  );

  const toggleTheme = () => {
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', next);
      return next;
    });
  };

  return (
    <AuthProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <AppLayout mode={mode} onToggleTheme={toggleTheme}>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/provider-register" element={<ProviderRegister />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/providers/:id" element={<ProviderDetails />} />
            <Route path="/" element={<Dashboard />} />
            <Route path="/announcements" element={<Announcements />} />
            <Route path="/providers" element={<ServiceProviders />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancelled" element={<PaymentCancelled />} />

            {/* Protected Routes (services) */}
            <Route path="/transferhub" element={<TransferHub />} />
            <Route path="/transferhub/profile" element={<ProtectedRoute><TransferHubProfile /></ProtectedRoute>} />
            <Route path="/transferhub/profiles" element={<TransferHubLanding />} />
            <Route path="/transferhub/profiles/:profileId" element={<ProtectedRoute><TransferHubProfileView /></ProtectedRoute>} />
            <Route path="/transferhub/jobs" element={<ProtectedRoute><TransferHubJobs /></ProtectedRoute>} />
            <Route path="/transferhub/requests" element={<ProtectedRoute><TransferHubRequests /></ProtectedRoute>} />
            <Route path="/transferhub/matches" element={<ProtectedRoute><TransferHubMatches /></ProtectedRoute>} />
            <Route path="/transferhub/conversations" element={<ProtectedRoute><NewMessaging /></ProtectedRoute>} />
            <Route path="/transferhub/search" element={<ProtectedRoute><TransferHubSearch /></ProtectedRoute>} />
            <Route path="/bookings" element={<ProtectedRoute><Bookings /></ProtectedRoute>} />
            
            {/* New Messaging and Matching System Test Routes */}
            <Route path="/test-messaging" element={<ProtectedRoute><NewMessaging /></ProtectedRoute>} />
            <Route path="/test-matching" element={<ProtectedRoute><NewMatching /></ProtectedRoute>} />
            <Route path="/provider-dashboard" element={<ProtectedRoute><ProviderDashboard /></ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute><NewMessaging /></ProtectedRoute>} />
            
            {/* Admin Routes */}
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
            <Route path="/admin/reviews" element={<ProtectedRoute><ReviewManagement /></ProtectedRoute>} />
            <Route path="/admin/banner" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
            <Route path="/admin/promotions" element={<ProtectedRoute><AdminPromotions /></ProtectedRoute>} />

            {/* Redirect to login if no route matches */}
            <Route path="*" element={<Login />} />
          </Routes>
        </AppLayout>
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
